/**
 * The account the /demo tour runs on: Republic Plumbing of Madison, WI, owned
 * by Sarah Mitchell — the same business the invite fixtures use
 * (components/invite/kit.tsx `DEMO_INVITES`), so a visitor who came from an
 * invite preview meets the same name twice.
 *
 * Nothing here is presented as a real customer's data: the demo bar above the
 * dashboard says "sample data" on every screen.
 */

import type { SiteIdentity } from '@foundlio/pack';
import type { Website } from '../../types';
import type { SetupStatus } from '../../lib/setupFlow';
import type { KitManifest, PrintKitState } from '../../services/printKitService';
import { daysAgo } from './time';

export const DEMO_WEBSITE_ID = 'demo-website-1';
export const DEMO_SLUG = 'republic-plumbing-madison';
export const DEMO_DOMAIN = 'republicplumbingmadison.com';
export const DEMO_BUSINESS_NAME = 'Republic Plumbing';
export const DEMO_CONTACT_NAME = 'Sarah Mitchell';
export const DEMO_PHONE = '(608) 555-0188';

export const demoUser = {
  id: 'demo-user-1',
  email: 'sarah@republicplumbingmadison.com',
  name: DEMO_CONTACT_NAME,
  avatarUrl: null,
  emailVerified: true,
  role: 'user',
};

export const demoWebsite: Website = {
  id: DEMO_WEBSITE_ID,
  domain: DEMO_DOMAIN,
  currentActiveUrl: `https://${DEMO_DOMAIN}`,
  netlifyUrl: `https://${DEMO_SLUG}.foundlio.com`,
  websiteStatus: 'live',
  screenshot: '/demo-shots/valley-plumbing.png',
  status: 'active',
  contentSetupComplete: true,
  businessName: DEMO_BUSINESS_NAME,
  trade: 'plumbing',
  createdAt: daysAgo(126),
  lastUpdated: daysAgo(4),
  leadsCount: 8,
  newLeadsCount: 2,
  reviewsCount: 5,
  avgRating: 4.8,
  bookingEnabled: true,
};

export const demoSubscription = {
  id: 'demo-sub-1',
  planType: 'professional',
  status: 'active',
  amount: 19900,
  currentPeriodStart: daysAgo(12),
};

/** Setup finished long ago — the dashboard shows the running business, not a tracker. */
export const demoSetupStatus: SetupStatus = {
  stage: 'live',
  stageStartedAt: daysAgo(112),
  etaAt: null,
  orderRef: 'FDL-4821',
  businessName: DEMO_BUSINESS_NAME,
  websiteId: DEMO_WEBSITE_ID,
  plan: 'professional',
  monthly: 199,
  domain: DEMO_DOMAIN,
  customDomain: DEMO_DOMAIN,
  domainStatus: 'connected',
  previewUrl: `https://${DEMO_DOMAIN}`,
  screenshotUrl: '/demo-shots/valley-plumbing.png',
  completedSteps: 3,
  tasks: [
    {
      id: 'confirm-details',
      title: 'Confirm your business details',
      description: 'Hours, service area and the phone number calls should ring on.',
      owner: 'you',
      status: 'done',
    },
    {
      id: 'photos',
      title: 'Send us your photos',
      description: 'Real job photos beat stock every time.',
      owner: 'you',
      status: 'done',
    },
    {
      id: 'domain',
      title: 'Your domain',
      description: `${DEMO_DOMAIN} is connected and the certificate is issued.`,
      owner: 'you',
      status: 'done',
    },
    {
      id: 'gbp',
      title: 'Google Business Profile',
      description: 'Claimed and kept in step with the site.',
      owner: 'foundlio',
      status: 'done',
    },
  ],
  events: [
    { id: 'live', at: daysAgo(112), title: 'Your site is live', detail: DEMO_DOMAIN, actor: 'system' },
    { id: 'deploy-ready', at: daysAgo(118), title: 'Draft ready for your review', actor: 'team' },
    { id: 'site', at: daysAgo(126), title: 'Project created', detail: DEMO_DOMAIN, actor: 'system' },
  ],
  isMock: true,
};

export const demoDeployment = {
  id: 'demo-deploy-1',
  status: 'ready',
  detail: 'Published to Cloudflare',
  createdAt: daysAgo(4),
  updatedAt: daysAgo(4),
};

/**
 * Print-kit identity. Same shape the invite's welcome-pack section renders
 * from, so the thumbnails on /demo/print-kit are drawn by the real plate
 * components rather than mocked up.
 */
export const demoIdentity: SiteIdentity = {
  slug: DEMO_SLUG,
  domain: DEMO_DOMAIN,
  business: {
    name: DEMO_BUSINESS_NAME,
    phone: DEMO_PHONE,
    city: 'Madison',
    state: 'WI',
    vertical: 'plumbing',
    established: null,
    establishedIsEstimate: false,
    logo: null,
  },
  theme: {
    colors: 'crimson-bold',
    fonts: 'tech',
    logoFont: 'Space Grotesk',
    logoIsLogomark: false,
  },
  owner: DEMO_CONTACT_NAME,
};

const demoManifest: KitManifest = {
  slug: DEMO_SLUG,
  scene: 'demo',
  trade: 'plumbing',
  generatedAt: daysAgo(9),
  generatorVersion: 'demo',
  baseUrl: `https://${DEMO_DOMAIN}`,
  rgbNote: 'Rendered in RGB. Your printer converts to CMYK.',
  pieces: [
    {
      id: 'card',
      file: `${DEMO_SLUG}-card.pdf`,
      label: 'Business cards',
      trimIn: [3.5, 2],
      bleedIn: 0.125,
      pageIn: [3.75, 2.25],
      pages: 2,
      bytes: 412_336,
      sha256: 'demo',
      logoOk: true,
      warnings: [],
    },
    {
      id: 'hanger',
      file: `${DEMO_SLUG}-hanger.pdf`,
      label: 'Door hanger',
      trimIn: [4.25, 11],
      bleedIn: 0.125,
      pageIn: [4.5, 11.25],
      pages: 1,
      bytes: 587_104,
      sha256: 'demo',
      logoOk: true,
      warnings: [],
    },
    {
      id: 'magnet',
      file: `${DEMO_SLUG}-magnet.pdf`,
      label: 'Car magnet',
      trimIn: [12, 18],
      bleedIn: 0.125,
      pageIn: [12.25, 18.25],
      pages: 1,
      bytes: 498_220,
      sha256: 'demo',
      logoOk: true,
      warnings: [],
    },
    {
      id: 'sign',
      file: `${DEMO_SLUG}-sign.pdf`,
      label: 'Yard sign',
      trimIn: [24, 18],
      bleedIn: 0.125,
      pageIn: [24.25, 18.25],
      pages: 1,
      bytes: 533_871,
      sha256: 'demo',
      logoOk: true,
      warnings: [],
    },
  ],
};

export const demoPrintKit: PrintKitState = {
  status: 'ready',
  manifest: demoManifest,
  identity: demoIdentity,
};
